// CalendarRangePanel.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { resolveAppdeckEventColors } from 'views/calendar/shared/calendarEventColors';
import {
    getCalendarDateHourKey,
    getCalendarDateKey,
    getCalendarEventMetaLabel,
    getCalendarEventTooltip,
    getCalendarNodeTitle,
    layoutTimedCalendarEventsForDay,
} from 'views/calendar/shared/calendarUtils';

// CSS
import './CalendarRangePanel.css';

// Components
import { isSameDay } from 'date-fns';
import { HOUR_LABELS } from 'views/calendar/shared/calendarConstants';

/**
 * CalendarRangePanel
 *
 * @returns {ReactNode} The rendered component.
 */
const CalendarRangePanel = ({
    headerLabel,
    days,
    selectedDate,
    today,
    language,
    eventsByDate,
    allDayEventsByDate,
    timedEventsByDateHour,
    onSelectDate,
    onNodeClick,
    onNodeDoubleClick,
    onAddDate,
    onAddDateTime,
    canDragEvents = false,
    draggingItem = null,
    dragOverTargetKey = null,
    onEventDragStart,
    onEventDragEnd,
    onDropTargetDragOver,
    onMoveToTarget,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [hoveredHeaderDayKey, setHoveredHeaderDayKey] = useState(null);
    const [hoveredAllDayEventKey, setHoveredAllDayEventKey] = useState(null);
    const [hoveredCellKey, setHoveredCellKey] = useState(null);
    const [hoveredSlotEventKey, setHoveredSlotEventKey] = useState(null);
    const allDayMap = allDayEventsByDate || eventsByDate || new Map();

    const panelBorderColor = theme.backgroundMuted;
    const panelMutedBackground = theme.backgroundMuted;

    // ----- MEMOS ----- //

    const timedLayoutsByDayKey = useMemo(() => {
        const timedMap = timedEventsByDateHour || new Map();
        const layoutsByDay = new Map();

        (days || []).forEach((day) => {
            const dayKey = getCalendarDateKey(day);
            const dayItems = [];

            for (let hour = 0; hour < 24; hour += 1) {
                const hourItems = timedMap.get(getCalendarDateHourKey(day, hour)) || [];
                dayItems.push(...hourItems);
            }

            layoutsByDay.set(dayKey, layoutTimedCalendarEventsForDay(dayItems));
        });

        return layoutsByDay;
    }, [days, timedEventsByDateHour]);

    // ----- UTILITIES ----- //

    const isDraggingEvent = (item) => (
        draggingItem?.node?.key === item?.node?.key
        && draggingItem?.field?.name === item?.field?.name
    );

    const renderDraggableEventProps = (item) => ({
        draggable: Boolean(
          canDragEvents
          && !item?.isRecurringOccurrence
          && !item?.rawValue?.recurrence
        ),
        onDragStart: (event) => {
            event.stopPropagation();
            onEventDragStart?.(item, event);
        },
        onDragEnd: () => onEventDragEnd?.(),
    });

    // ----- RENDER ----- //

    return (
        <>

            {/* HEADER */}
            <div
              className="calendar-range-panel-header"
              style={{
                    background: panelMutedBackground,
                    borderBottom: `1px solid ${panelBorderColor}`,
                }}
            >

                {/* HEADER TIME */}
                <div
                  className="calendar-range-panel-header-time"
                >
                    {headerLabel}
                </div>
                {days.map((day) => {
                    const isSelected = isSameDay(day, selectedDate);
                    const isDayToday = isSameDay(day, today);
                    const dayKey = getCalendarDateKey(day);
                    return (
                        <button
                          className={[
                                'calendar-range-panel-header-day',
                                isSelected ? 'calendar-range-panel-header-day--selected' : '',
                                isDayToday ? 'calendar-range-panel-header-day--today' : '',
                            ].filter(Boolean).join(' ')}
                          style={{
                                background: isSelected
                                    ? theme.backgroundHover
                                    : hoveredHeaderDayKey === dayKey
                                        ? theme.backgroundMuted
                                        : 'transparent',
                            }}
                          key={dayKey}
                          type="button"
                          onClick={() => onSelectDate(day)}
                          onMouseEnter={() => setHoveredHeaderDayKey(dayKey)}
                          onMouseLeave={() => setHoveredHeaderDayKey(null)}
                        >

                            {/* HEADER DAY LABEL */}
                            <div
                              className="calendar-range-panel-header-day-label"
                            >
                                {day.toLocaleDateString(language || 'en', { weekday: 'short' })}
                            </div>

                            {/* HEADER DAY NUMBER */}
                            <div
                              className="calendar-range-panel-header-day-number"
                            >
                                {day.getDate()}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* ALL DAY */}
            <div
              className="calendar-range-panel-all-day"
              style={{
                    background: panelMutedBackground,
                    borderBottom: `1px solid ${panelBorderColor}`,
                }}
            >

                {/* ALL DAY LABEL */}
                <div
                  className="calendar-range-panel-all-day-label"
                >
                    All day
                </div>
                {days.map((day) => {
                    const dayKey = getCalendarDateKey(day);
                    const allDayDropKey = `allday:${dayKey}`;
                    const isAllDayDropTarget = dragOverTargetKey === allDayDropKey;
                    const items = allDayMap.get(dayKey) || [];
                    return (
                        <div
                          className={[
                                'calendar-range-panel-all-day-column',
                                isAllDayDropTarget ? 'calendar-range-panel-all-day-column--drop-target' : '',
                            ].filter(Boolean).join(' ')}
                          style={{
                                borderLeft: `1px solid ${panelBorderColor}`,
                                cursor: onAddDate ? 'pointer' : undefined,
                            }}
                          key={`allday-${dayKey}`}
                          onClick={() => {
                                if (draggingItem) return;
                                onAddDate?.(day);
                            }}
                          onDragOver={(event) => {
                                if (!canDragEvents || !draggingItem) return;
                                onDropTargetDragOver?.(allDayDropKey, event);
                            }}
                          onDrop={(event) => {
                                if (!canDragEvents) return;
                                event.preventDefault();
                                event.stopPropagation();
                                onMoveToTarget?.(day, { targetKey: allDayDropKey });
                            }}
                        >
                            {items.map((item, index) => {
                                const eventColors = resolveAppdeckEventColors(item, index);
                                const allDayEventKey = `${dayKey}-${index}`;
                                const isHovered = hoveredAllDayEventKey === allDayEventKey;

                                return (
                                <button
                                  className={[
                                        'calendar-range-panel-event',
                                        isDraggingEvent(item) ? 'calendar-range-panel-event--dragging' : '',
                                    ].filter(Boolean).join(' ')}
                                  style={{
                                        background: eventColors.background,
                                        color: eventColors.foreground,
                                        borderColor: isHovered
                                            ? eventColors.foreground
                                            : panelBorderColor,
                                    }}
                                  key={`${item.node?.key || 'node'}-${item.field?.name || 'date'}-${index}`}
                                  type="button"
                                  {...renderDraggableEventProps(item)}
                                  onClick={(event) => {
                                        event.stopPropagation();
                                        onNodeClick?.(event, item);
                                    }}
                                  onDoubleClick={(event) => {
                                        event.stopPropagation();
                                        onNodeDoubleClick?.(event, item);
                                    }}
                                  onMouseEnter={() => setHoveredAllDayEventKey(allDayEventKey)}
                                  onMouseLeave={() => setHoveredAllDayEventKey(null)}
                                >

                                    {/* EVENT TITLE */}
                                    <div
                                      className="calendar-range-panel-event-title"
                                      style={{ color: eventColors.foreground }}
                                    >
                                        {getCalendarNodeTitle(item.node)}
                                    </div>

                                    {/* EVENT META */}
                                    <div
                                      className="calendar-range-panel-event-meta"
                                      style={{ color: eventColors.foreground }}
                                    >
                                        {getCalendarEventMetaLabel(item)}
                                    </div>
                                </button>
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {/* GRID */}
            <div className="calendar-range-panel-grid">
                {HOUR_LABELS.map((label, index) => (
                    <React.Fragment key={label}>

                        {/* TIME */}
                        <div
                          className="calendar-range-panel-time"
                          style={{
                            background: panelMutedBackground,
                            borderTop: `1px solid ${panelBorderColor}`,
                          }}
                        >
                            {label}
                        </div>
                        {days.map((day) => {
                            const dayKey = getCalendarDateKey(day);
                            const slotDropKey = `slot:${getCalendarDateHourKey(day, index)}`;
                            const isSlotDropTarget = dragOverTargetKey === slotDropKey;

                            return (
                                <div
                                  className={[
                                        'calendar-range-panel-cell',
                                        isSlotDropTarget ? 'calendar-range-panel-cell--drop-target' : '',
                                    ].filter(Boolean).join(' ')}
                                  key={`${dayKey}-${index}`}
                                  onDragOver={(event) => {
                                        if (!canDragEvents || !draggingItem) return;
                                        onDropTargetDragOver?.(slotDropKey, event);
                                    }}
                                  onDrop={(event) => {
                                        if (!canDragEvents) return;
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onMoveToTarget?.(day, { targetHour: index, targetKey: slotDropKey });
                                    }}
                                >

                                    {/* CELL HITBOX */}
                                    <button
                                      className="calendar-range-panel-cell-hitbox"
                                      style={{
                                            background: isSlotDropTarget
                                                ? theme.backgroundHover
                                                : hoveredCellKey === `${dayKey}-${index}`
                                                    ? theme.backgroundMuted
                                                    : 'transparent',
                                            borderLeft: `1px solid ${panelBorderColor}`,
                                            borderTop: `1px solid ${panelBorderColor}`,
                                        }}
                                      type="button"
                                      onClick={() => {
                                            if (draggingItem) return;
                                            const slotDate = new Date(day);
                                            slotDate.setHours(index, 0, 0, 0);
                                            onAddDateTime?.(slotDate);
                                        }}
                                      onMouseEnter={() => setHoveredCellKey(`${dayKey}-${index}`)}
                                      onMouseLeave={() => setHoveredCellKey(null)}
                                    />
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}

                {/* TIMED OVERLAY */}
                <div className="calendar-range-panel-timed-overlay">
                    {days.map((day) => {
                        const dayKey = getCalendarDateKey(day);
                        const layouts = timedLayoutsByDayKey.get(dayKey) || [];

                        return (
                            <div
                              className="calendar-range-panel-timed-day"
                              key={`timed-${dayKey}`}
                            >
                                {layouts.map((layout, layoutIndex) => {
                                    const { item } = layout;
                                    const title = getCalendarNodeTitle(item.node);
                                    const metaLabel = getCalendarEventMetaLabel(item);
                                    const tooltip = getCalendarEventTooltip(item);
                                    const eventKey = `${item.node?.key || 'node'}-${item.field?.name || 'date'}-${layoutIndex}`;
                                    const widthPercent = 100 / layout.columnCount;
                                    const leftPercent = widthPercent * layout.columnIndex;
                                    const eventColors = resolveAppdeckEventColors(item, layoutIndex);

                                    return (
                                        <button
                                          className={[
                                                'calendar-range-panel-slot-event',
                                                isDraggingEvent(item) ? 'calendar-range-panel-slot-event--dragging' : '',
                                            ].filter(Boolean).join(' ')}
                                          style={{
                                                background: eventColors.background,
                                                color: eventColors.foreground,
                                                borderColor: hoveredSlotEventKey === eventKey
                                                    ? eventColors.foreground
                                                    : panelBorderColor,
                                                height: `calc(var(--calendar-range-hour-row-min, 40px) * ${layout.durationMinutes} / 60)`,
                                                left: `calc(${leftPercent}% + 3px)`,
                                                top: `calc(var(--calendar-range-hour-row-min, 40px) * ${layout.startMinutes} / 60)`,
                                                width: `calc(${widthPercent}% - 6px)`,
                                            }}
                                          key={eventKey}
                                          type="button"
                                          title={tooltip}
                                          {...renderDraggableEventProps(item)}
                                          onClick={(event) => {
                                                event.stopPropagation();
                                                onNodeClick?.(event, item);
                                            }}
                                          onDoubleClick={(event) => {
                                                event.stopPropagation();
                                                onNodeDoubleClick?.(event, item);
                                            }}
                                          onMouseEnter={() => setHoveredSlotEventKey(eventKey)}
                                          onMouseLeave={() => setHoveredSlotEventKey(null)}
                                        >

                                            {/* SLOT EVENT TITLE */}
                                            <span
                                              className="calendar-range-panel-slot-event-title"
                                            >
                                                {title}
                                            </span>

                                            {/* SLOT EVENT META */}
                                            <span
                                              className="calendar-range-panel-slot-event-meta"
                                            >
                                                {metaLabel}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

// Dev Tools
CalendarRangePanel.displayName = 'CalendarRangePanel';

export default CalendarRangePanel;
